/**
 * CreateDebitNote.tsx
 *
 * Professional debit note creation screen.
 * Mirrors CreateCreditNote but with DR accounting logic.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";

import {
  Alert, Autocomplete, Box, Button, Card, CardContent,
  Chip, CircularProgress, Container, Divider, FormControl,
  Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from "@mui/material";

import AddIcon        from "@mui/icons-material/Add";
import ArrowBackIcon  from "@mui/icons-material/ArrowBack";
import DeleteIcon     from "@mui/icons-material/Delete";
import SaveIcon       from "@mui/icons-material/Save";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getUsers }       from "../Services/userService";
import { getOutstanding } from "../Services/InvoiceReceiptService";
import { createDebitNote } from "../Services/CreditDebitNoteService";
import { getOrganizationDetails } from "../Services/orgService";
import {
  INoteItem,
  DebitNoteReason,
} from "../Interfaces/ICreditDebitNote";

// ─── Constants ────────────────────────────────────────────────────────────────

const DEBIT_REASONS: { value: DebitNoteReason; label: string }[] = [
  { value: "UNDERBILLING",       label: "Underbilling Correction" },
  { value: "ADDITIONAL_CHARGES", label: "Additional Charges" },
  { value: "PRICE_REVISION",     label: "Price Revision" },
  { value: "OTHER",              label: "Other" },
];

const TAX_RATES = [0, 3, 5, 12, 18, 28];

const emptyItem = (): INoteItem => ({
  itemId: "", description: "", hsn: "",
  quantity: 1, rate: 0, discount: 0, tax: 0, amount: 0,
});

const computeAmount = (item: INoteItem): number =>
  item.quantity * item.rate * (1 - item.discount / 100) * (1 + item.tax / 100);

const fmt = (n: number) =>
  `Rs. ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

// ─── PDF ─────────────────────────────────────────────────────────────────────

function printDebitNotePDF(dnId: string, data: any, orgDetails: any) {
  const doc = new jsPDF();
  const W   = doc.internal.pageSize.width;
  const H   = doc.internal.pageSize.height;

  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text("DEBIT NOTE", W / 2, 12, { align: "center" });

  // Org box
  doc.rect(14, 18, 88, 44);
  doc.setFontSize(11); doc.text(orgDetails?.OrgName || "N/A", 16, 26);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const addr = `${orgDetails?.address?.[0]?.addressLine1 || ""}, ${orgDetails?.address?.[0]?.city || ""}, ${orgDetails?.address?.[0]?.state || ""}`;
  doc.text(doc.splitTextToSize(addr, 84), 16, 32);
  doc.text(`GSTIN: ${orgDetails?.GST || ""}`, 16, 42);
  doc.text(`Email: ${orgDetails?.email || ""}`, 16, 47);
  doc.text(`Phone: ${orgDetails?.phone || ""}`, 16, 52);

  // DN details box
  doc.rect(108, 18, 88, 44);
  doc.setFont("helvetica", "bold");
  doc.text("Debit Note No.:", 110, 26); doc.setFont("helvetica", "normal");
  doc.text(dnId, 110, 32);
  doc.setFont("helvetica", "bold"); doc.text("Date:", 110, 40); doc.setFont("helvetica", "normal");
  doc.text(new Date(data.dnDate).toLocaleDateString("en-GB"), 110, 46);
  doc.setFont("helvetica", "bold"); doc.text("Against Invoice:", 110, 54); doc.setFont("helvetica", "normal");
  doc.text(data.referenceId, 110, 60);

  // Customer
  let y = 72;
  doc.rect(14, y, 182, 30);
  doc.setFont("helvetica", "normal"); doc.text("Customer:", 16, y + 7);
  doc.setFontSize(11); doc.setFont("helvetica", "bold");
  doc.text(data.customerName, 16, y + 14);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text(`Reason: ${DEBIT_REASONS.find(r => r.value === data.reason)?.label || data.reason}`, 16, y + 22);

  // Items header
  y = 110;
  doc.setFont("helvetica", "bold");
  ["#","Description","HSN","Qty","Rate","Tax%","Amount"].forEach((h, i) => {
    const xs = [14, 19, 89, 104, 118, 138, 153];
    const ws = [5, 70, 15, 14, 20, 15, 43];
    doc.rect(xs[i], y, ws[i], 8);
    doc.text(h, xs[i] + 1, y + 5.5);
  });

  // Items
  doc.setFont("helvetica", "normal");
  data.items.forEach((item: INoteItem, i: number) => {
    y += 8;
    const cells = [`${i+1}`, item.description, item.hsn||"", `${item.quantity}`, `${item.rate}`, `${item.tax}%`, item.amount.toFixed(2)];
    const xs = [14, 19, 89, 104, 118, 138, 153];
    const ws = [5, 70, 15, 14, 20, 15, 43];
    cells.forEach((cell, ci) => {
      doc.rect(xs[ci], y, ws[ci], 8);
      doc.text(cell, xs[ci] + 1, y + 5.5);
    });
  });

  // Totals
  y += 14;
  doc.setFont("helvetica", "bold");
  doc.text(`Subtotal: ${fmt(data.subTotal)}`, W - 80, y);
  doc.text(`Tax: ${fmt(data.totalTax)}`,      W - 80, y + 7);
  doc.text(`Grand Total (Debit): ${fmt(data.grandTotal)}`, W - 80, y + 14);

  if (data.notes) {
    y += 28;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(`Notes: ${data.notes}`, 14, y);
  }

  doc.setFontSize(8); doc.setTextColor(150);
  doc.text("This is a Computer Generated Debit Note", W / 2, H - 5, { align: "center" });
  doc.setTextColor(0);
  doc.save(`DebitNote_${dnId}.pdf`);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Customer { Id: string; customerName?: string; companyName?: string; phone?: string; }
interface Invoice  { invoiceId: string; grandTotal: number; dueAmount: number; status: string; }

const CreateDebitNote: React.FC = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));

  const orgId     = localStorage.getItem("orgId")  ?? "";
  const createdBy = localStorage.getItem("userId") ?? orgId;
  //const orgDetails= JSON.parse(localStorage.getItem("orgDetails") || "{}");
  const [orgDetails, setOrgDetails] = useState<any>(null);
  const [customers,        setCustomers]        = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [invoices,         setInvoices]         = useState<Invoice[]>([]);
  const [selectedInvoice,  setSelectedInvoice]  = useState<Invoice | null>(null);
  const [reason,           setReason]           = useState<DebitNoteReason>("UNDERBILLING");
  const [dnDate,           setDnDate]           = useState(new Date().toISOString().split("T")[0]);
  const [notes,            setNotes]            = useState("");
  const [items,            setItems]            = useState<INoteItem[]>([emptyItem()]);
  const [submitting,       setSubmitting]       = useState(false);
  const [error,            setError]            = useState("");
  const [successDnId,      setSuccessDnId]      = useState("");

     //Org Load
    useEffect(() => {
    if (!orgId) return;
    getOrganizationDetails(orgId)
      .then((data) => {
        setOrgDetails(data);
      })
      .catch((err) => {
        console.error("Failed to load org details", err);
      });
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    getUsers(orgId, "customer").then((d) => setCustomers(d ?? [])).catch(console.error);
  }, [orgId]);

  useEffect(() => {
    if (!selectedCustomer || !orgId) { setInvoices([]); setSelectedInvoice(null); return; }
    getOutstanding(orgId, selectedCustomer.Id)
      .then((d) => setInvoices(d as any ?? []))
      .catch(() => setInvoices([]));
  }, [selectedCustomer, orgId]);

  const updateItem = (idx: number, field: keyof INoteItem, value: any) => {
    setItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      updated.amount = computeAmount(updated);
      return updated;
    }));
  };

  const addItem    = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (idx: number) => setItems((p) => p.filter((_, i) => i !== idx));

  const subTotal   = items.reduce((s, i) => s + i.quantity * i.rate * (1 - i.discount / 100), 0);
  const totalTax   = items.reduce((s, i) => s + i.quantity * i.rate * (1 - i.discount / 100) * (i.tax / 100), 0);
  const totalDisc  = items.reduce((s, i) => s + i.quantity * i.rate * (i.discount / 100), 0);
  const grandTotal = subTotal + totalTax;

  const handleSubmit = async () => {
    if (!selectedCustomer || !selectedInvoice || grandTotal <= 0) {
      setError("Please select customer, invoice and add at least one item.");
      return;
    }
    setSubmitting(true); setError("");
    try {
      const payload = {
        orgId,
        customerId:    selectedCustomer.Id,
        customerName:  selectedCustomer.customerName ?? selectedCustomer.companyName ?? "",
        referenceId:   selectedInvoice.invoiceId,
        reason,
        dnDate,
        items:         items.map((i) => ({ ...i, amount: computeAmount(i) })),
        subTotal:      parseFloat(subTotal.toFixed(2)),
        totalTax:      parseFloat(totalTax.toFixed(2)),
        totalDiscount: parseFloat(totalDisc.toFixed(2)),
        grandTotal:    parseFloat(grandTotal.toFixed(2)),
        notes,
        createdBy,
      };
      const res = await createDebitNote(payload);
      setSuccessDnId(res.dnId);
      printDebitNotePDF(res.dnId, payload, orgDetails);
    } catch (err: any) {
      setError(err?.message || "Failed to create debit note.");
    } finally {
      setSubmitting(false);
    }
  };

  const custLabel = (c: Customer) =>
    `${c.customerName ?? c.companyName ?? ""}${c.phone ? " | " + c.phone : ""}`;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate(-1)} sx={{ textTransform: "none" }}>Back</Button>
        <Typography variant="caption" color="text.secondary">Receipt › Debit Note</Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <ReceiptLongIcon color="error" />
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} color="error.main">
          Create Debit Note
        </Typography>
        <Chip label="DR" color="error" size="small" />
      </Stack>

      {successDnId && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Debit Note <strong>{successDnId}</strong> created and PDF downloaded.{" "}
          <Button size="small" onClick={() => { setSuccessDnId(""); setItems([emptyItem()]); setSelectedCustomer(null); }}>
            Create Another
          </Button>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {/* Customer + Invoice */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                CUSTOMER & INVOICE DETAILS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={customers} getOptionLabel={custLabel}
                    value={selectedCustomer}
                    onChange={(_, v) => { setSelectedCustomer(v); setSelectedInvoice(null); }}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    renderInput={(params) => <TextField {...params} label="Customer *" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={invoices} disabled={!selectedCustomer}
                    getOptionLabel={(inv: any) => `${inv.invoiceId} — Due: ₹${Number(inv.dueAmount).toLocaleString("en-IN")}`}
                    value={selectedInvoice} onChange={(_, v) => setSelectedInvoice(v)}
                    isOptionEqualToValue={(o: any, v: any) => o.invoiceId === v.invoiceId}
                    renderInput={(params) => <TextField {...params} label="Against Invoice *" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reason *</InputLabel>
                    <Select value={reason} label="Reason *" onChange={(e) => setReason(e.target.value as DebitNoteReason)}>
                      {DEBIT_REASONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Debit Note Date *" type="date" size="small" fullWidth
                    value={dnDate} onChange={(e) => setDnDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">ITEMS / CHARGES</Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" color="error" onClick={addItem}>
                  Add Row
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "grey.50" }}>
                    <TableRow>
                      {["#","Description","HSN","Qty","Rate (₹)","Disc %","Tax %","Amount (₹)",""].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, fontSize: 11 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" placeholder="Description"
                            value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)}
                            sx={{ minWidth: 120 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" value={item.hsn}
                            onChange={(e) => updateItem(idx, "hsn", e.target.value)} sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" type="number" value={item.quantity}
                            onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0 }} sx={{ width: 55 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" type="number" value={item.rate}
                            onChange={(e) => updateItem(idx, "rate", parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0 }} sx={{ width: 75 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" type="number" value={item.discount}
                            onChange={(e) => updateItem(idx, "discount", parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, max: 100 }} sx={{ width: 55 }} />
                        </TableCell>
                        <TableCell>
                          <Select size="small" variant="standard" value={item.tax}
                            onChange={(e) => updateItem(idx, "tax", Number(e.target.value))} sx={{ width: 65 }}>
                            {TAX_RATES.map((r) => <MenuItem key={r} value={r}>{r}%</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "error.main" }}>
                          {computeAmount(item).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => removeItem(idx)}
                              disabled={items.length === 1}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>NOTES</Typography>
              <TextField multiline rows={3} fullWidth size="small"
                placeholder="Internal notes or charge details..."
                value={notes} onChange={(e) => setNotes(e.target.value)} />
            </CardContent>
          </Card>
        </Grid>

        {/* Summary */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, position: { md: "sticky" }, top: { md: 80 } }}>
            <Box sx={{ bgcolor: "error.main", px: 2, py: 1.5, borderRadius: "8px 8px 0 0" }}>
              <Typography color="white" fontWeight={700}>Debit Note Summary</Typography>
            </Box>
            <CardContent>
              {selectedInvoice && (
                <>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Invoice Ref</Typography>
                    <Typography variant="body2" fontWeight={500}>{(selectedInvoice as any).invoiceId}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" color="text.secondary">Current Due</Typography>
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      {fmt((selectedInvoice as any).dueAmount)}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 1.5 }} />
                </>
              )}
              {[
                { label: "Subtotal", value: subTotal },
                { label: "Discount", value: totalDisc },
                { label: "Tax",      value: totalTax },
              ].map(({ label, value }) => (
                <Stack key={label} direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{fmt(value)}</Typography>
                </Stack>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography fontWeight={700}>Debit Amount</Typography>
                <Typography fontWeight={700} color="error.main" variant="h6">{fmt(grandTotal)}</Typography>
              </Stack>
              {grandTotal > 0 && selectedInvoice && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
                  After this debit note, invoice due will be{" "}
                  <strong>{fmt((selectedInvoice as any).dueAmount + grandTotal)}</strong>
                </Alert>
              )}
              <Button
                variant="contained" color="error" fullWidth size="large"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={!selectedCustomer || !selectedInvoice || grandTotal <= 0 || submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Saving..." : "Create Debit Note"}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CreateDebitNote;